import { useEffect, useRef, useState } from 'react'
import { clarifyCommit, clarifyTurn, markDone } from '../api/client'
import type { Conversation, PlanAction, Project } from '../types'

/**
 * L'atelier : le back peut renvoyer soit le plan complet, soit seulement la/les
 * nouvelle(s) action(s) du tour. On fusionne par titre (mise à jour de l'existant,
 * ajout du nouveau) au lieu d'écraser — le plan des marches vers l'outcome grandit
 * et reste visible en entier.
 */
export function mergeActions(existing: PlanAction[], incoming: PlanAction[] | undefined): PlanAction[] {
  const out = existing.slice()
  for (const a of incoming ?? []) {
    const i = out.findIndex((x) => x.title === a.title)
    if (i >= 0) out[i] = a
    else out.push(a)
  }
  return out
}

interface Props {
  /** Item à re-clarifier ; sinon le back prend la plus ancienne capture. */
  startItemId: string | null
  onStartConsumed: () => void
}

export default function Coach({ startItemId, onStartConsumed }: Props) {
  const [conv, setConv] = useState<Conversation | null>(null)
  const [loading, setLoading] = useState(true)
  const [empty, setEmpty] = useState(false)
  const [busy, setBusy] = useState(false)
  const [precision, setPrecision] = useState<string | null>(null)
  const [picker, setPicker] = useState(false)
  const [newProj, setNewProj] = useState<string | null>(null)
  const opened = useRef(false)

  useEffect(() => {
    if (opened.current) return
    opened.current = true
    void open(startItemId)
    onStartConsumed()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function open(itemId: string | null) {
    setLoading(true)
    try {
      const d = await clarifyTurn({ history: [], ...(itemId ? { item_id: itemId } : {}) })
      if (d.empty || !d.item) {
        setConv(null)
        setEmpty(true)
        return
      }
      setEmpty(false)
      setConv({
        itemId: d.item.id,
        itemTitle: d.item.title,
        history: [],
        brain: d.brain ?? 'heuristic',
        remaining: d.remaining ?? 0,
        message: d.message ?? '',
        options: d.options ?? [],
        actions: d.actions ?? [],
        project: d.project ?? null,
        domain: d.domain ?? null,
        done: !!d.done,
        projects: d.active_projects ?? [],
        attached: false,
      })
    } catch {
      /* 401 : l'app repasse par le login */
    } finally {
      setLoading(false)
    }
  }

  async function answer(reply: string, hint: string | null = null) {
    if (busy || !conv) return
    setBusy(true)
    const history = [...conv.history, { q: conv.message, a: hint ? '(précision) ' + hint : reply }]
    try {
      const d = await clarifyTurn({ item_id: conv.itemId, history, hint })
      if (d.empty) {
        setConv(null)
        setEmpty(true)
        return
      }
      setPrecision(null)
      setConv({
        ...conv,
        history,
        message: d.message ?? '',
        options: d.options ?? [],
        actions: mergeActions(conv.actions, d.actions),
        domain: d.domain ?? null,
        done: !!d.done,
        remaining: d.remaining ?? conv.remaining,
        brain: d.brain ?? conv.brain,
        projects: d.active_projects ?? conv.projects,
        // rattachement manuel prioritaire sur la détection coach
        project: conv.attached ? conv.project : d.project ?? null,
      })
    } catch {
      /* idem */
    } finally {
      setBusy(false)
    }
  }

  /** Rien n'est écrit tant que l'utilisateur n'a pas validé. */
  /**
   * « Déjà fait » : on s'aperçoit en pleine clarification que la tâche est
   * derrière soi. On la clôt et on enchaîne, sans enregistrer le plan en cours —
   * finir de clarifier quelque chose de fait est du travail pour rien.
   * /api/done pose clarified_at au passage : elle ne reviendra pas dans l'inbox.
   */
  async function alreadyDone() {
    if (busy || !conv) return
    setBusy(true)
    setLoading(true)
    try {
      await markDone(conv.itemId)
      setConv(null)
      setPicker(false)
      setNewProj(null)
      await open(null) // capture suivante
    } catch {
      setLoading(false)
    } finally {
      setBusy(false)
    }
  }

  async function commit() {
    if (busy || !conv) return
    setBusy(true)
    setLoading(true)
    try {
      await clarifyCommit({
        item_id: conv.itemId,
        project: conv.project,
        actions: conv.actions,
        domain: conv.domain,
      })
      setConv(null)
      setPicker(false)
      setNewProj(null)
      await open(null) // capture suivante
    } catch {
      setLoading(false)
    } finally {
      setBusy(false)
    }
  }

  function attach(p: Project) {
    if (!conv) return
    setConv({ ...conv, project: { id: p.id, title: p.title, outcome: p.outcome }, attached: true })
    setPicker(false)
  }

  // pas d'id => le back crée le projet au commit (rien n'est écrit avant Valider)
  function createProject() {
    const title = (newProj ?? '').trim()
    if (!title || !conv) return
    setConv({ ...conv, project: { title, outcome: title }, attached: true })
    setNewProj(null)
    setPicker(false)
  }

  function detach() {
    if (!conv) return
    setConv({ ...conv, project: null, attached: false })
  }

  if (loading) {
    return (
      <div className="coach">
        <div className="count">Le coach réfléchit…</div>
      </div>
    )
  }
  if (empty || !conv) {
    return (
      <div className="done">
        <div className="big">✨</div>
        <p>
          Inbox vide.
          <br />
          Rien à clarifier — tu peux souffler.
        </p>
      </div>
    )
  }

  const brain = conv.brain === 'claude' || conv.brain === 'gemini' ? conv.brain : 'heuristique'
  const single = conv.actions.length === 1 && !conv.project

  const attachBar = (
    <>
      {conv.attached && conv.project && (
        <div className="attached">
          Rattaché à « {conv.project.title} »
          <button className="detach" onClick={detach}>
            ✕
          </button>
        </div>
      )}
      <button className="opt other" onClick={() => setPicker(!picker)}>
        📁 Rattacher à un projet…
      </button>
      {picker && (
        <div className="projpicker">
          {conv.projects.map((p) => (
            <button key={p.id} className="pchip" onClick={() => attach(p)}>
              📁 {p.title}
            </button>
          ))}
          <button className="pchip new" onClick={() => setNewProj('')}>
            ＋ Nouveau projet
          </button>
        </div>
      )}
      {newProj !== null && (
        <div className="newproj">
          <input
            autoFocus
            placeholder="Nom du nouveau projet…"
            value={newProj}
            onChange={(e) => setNewProj(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && createProject()}
          />
          <button className="ebtn" onClick={createProject}>
            Créer
          </button>
        </div>
      )}
    </>
  )

  const precisionBox = precision !== null && (
    <div className="precision">
      <input
        autoFocus
        placeholder="Dis au coach…"
        value={precision}
        onChange={(e) => setPrecision(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && precision.trim()) void answer('(précision)', precision.trim())
        }}
      />
      <button className="ebtn" onClick={() => precision.trim() && void answer('(précision)', precision.trim())}>
        Envoyer
      </button>
    </div>
  )

  return (
    <div className="coach">
      <div className="count">
        {conv.remaining} à clarifier · coach {brain}
      </div>
      <div className="item">↳ {conv.itemTitle}</div>
      {conv.actions.length > 0 && (
        <div className="projpanel">
          <div className="pt">
            {single ? '✅' : '📁'} {conv.project ? conv.project.title : single ? 'Action unique' : 'En construction'}
          </div>
          <div className="psteps">
            {conv.actions.map((a, i) => (
              <div key={i} className={'pstep' + (a.blocked ? ' up' : '')}>
                {a.blocked ? '⏳' : '→'} {a.title}
                {a.context && <span className="pctx"> {a.context}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="bubble">
        {conv.done ? conv.message || 'Voilà ce que je propose. Tu valides ?' : conv.message}
      </div>
      <div className={'opts' + (busy ? ' busy' : '')}>
        {conv.done ? (
          <>
            <button className="opt valider" onClick={() => void commit()}>
              <div className="lab">✓ Valider et enregistrer</div>
            </button>
            <button className="opt other" onClick={() => setPrecision(precision === null ? '' : null)}>
              Modifier / préciser
            </button>
            <button className="opt other" onClick={() => void alreadyDone()}>
              ☑ déjà fait — clore
            </button>
          </>
        ) : (
          <>
            {conv.options.map((o, i) => (
              <button key={i} className="opt" onClick={() => void answer(o)}>
                <div className="lab">{o}</div>
              </button>
            ))}
            <button className="opt other" onClick={() => setPrecision(precision === null ? '' : null)}>
              Préciser autre chose
            </button>
            <button className="opt other" onClick={() => void alreadyDone()}>
              ☑ déjà fait — clore
            </button>
            {/* Le coach ne retient jamais : dès qu'il y a une action au plan, on peut
                couper et enregistrer, sans attendre son `done`. */}
            {conv.actions.length > 0 && (
              <button className="opt valider" onClick={() => void commit()}>
                <div className="lab">✓ C'est bon, enregistre</div>
              </button>
            )}
          </>
        )}
        {attachBar}
      </div>
      {precisionBox}
    </div>
  )
}
